import React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components/macro";
import { useTranslation } from "react-i18next";

import useOrganizations from "contexts/Organizations/useOrganizations";
import useProjects from "contexts/Projects/useProjects";
import useConfirmLeaveScene from "hooks/useConfirmLeaveScene";
import useIsSingleProjectConsumer from "hooks/useIsSingleProjectConsumer";
import { PROJECTS, SCENES } from "common/routes.constants";
import OrganizationSwitcher from "../OrganizationSwitcher/OrganizationSwitcher";
import ProjectSwitcher from "../ProjectSwitcher/ProjectSwitcher";
import SceneSwitcher from "../SceneSwitcher/SceneSwitcher";
import ShareButton from "./ShareButton";
import BmsAppointmentButton from "./BmsAppointmentButton";
import LiveViewButton from "./LiveViewButton";
import UserMenu from "./UserMenu";

const Header = () => {
	const { current } = useOrganizations();
	const { current: currentProject } = useProjects();
	const { t } = useTranslation();
	const history = useHistory();
	const confirmLeaveScene = useConfirmLeaveScene();

	// The switcher would only ever have one thing to switch to (see
	// ProjectSwitcher.tsx's "All projects" hiding) — dropping it removes a
	// dead-end control, one fewer place a b2c user can get lost in. Same
	// condition as the project page's back arrow, hence the shared hook.
	const hideProjectSwitcher = useIsSingleProjectConsumer();

	// The logo doubles as the way home, and "home" is one step shallower for a
	// personal org: its gallery holds the single project it will ever have, so
	// sending a b2c user there is the same dead end the project switcher is
	// hidden for. They go to that project's scenes instead — the shallowest
	// screen that is actually theirs.
	const homePath =
		hideProjectSwitcher && currentProject?.shortid
			? `/${PROJECTS}/${currentProject.shortid}/${SCENES}`
			: `/${PROJECTS}`;
	const homeLabel = String(
		t(
			homePath === `/${PROJECTS}`
				? "app.leaveScene.tooltipProjects"
				: "app.leaveScene.tooltipScenes",
		),
	);

	const handleLogoClick = () => {
		confirmLeaveScene(() => history.push(homePath));
	};

	const originalThumbnail = `${process.env.REACT_APP_AWS_S3_URL}/organizations/${current?.shortid}/${current?.meta?.avatar}`;

	return (
		<StyledHeader
			data-component="Header"
			data-path="src/components/Header/Header.tsx"
			$hideProjectSwitcher={hideProjectSwitcher}
		>
			<StyledOrganizationSwitcherContainer>
				{current && (
					<StyledLogoButton
						type="button"
						onClick={handleLogoClick}
						title={homeLabel}
						aria-label={homeLabel}
					>
						<OrganizationLogo url={originalThumbnail} />
					</StyledLogoButton>
				)}
				<OrganizationSwitcher />
			</StyledOrganizationSwitcherContainer>
			{!hideProjectSwitcher && <ProjectSwitcher />}
			<SceneSwitcher />
			<span>{/* Do not delete this span */}</span>
			<LiveViewButton />
			<BmsAppointmentButton />
			<ShareButton />
			<UserMenu />
		</StyledHeader>
	);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const OrganizationLogo = ({ url }: { url: string }) => {
	const fallbackThumbnail = "/images/hegias-logo.svg";

	return (
		<img
			src={url}
			alt="Organization logo"
			aria-hidden
			role="presentation"
			onError={(e: any) => {
				e.target.src = fallbackThumbnail;
				e.target.style = "object-fit: contain;";
			}}
		/>
	);
};

// Columns: [OrgSwitcher /w Logo] - [ProjectSwitcher] - [SceneSwitcher] - [ ... ] - [Live View] [Appointments] [Share] [User]
// Library moved into UserMenu's dropdown — see UserMenu.tsx.
// ProjectSwitcher is omitted entirely for a consumer with a single project (see
// hideProjectSwitcher above), so the column count must shrink by one to match —
// otherwise the grid's implicit auto-placement shifts every following item left
// by one track, squeezing UserMenu into ShareButton's narrower column. This one
// can flip after the first render, when the project count arrives.
const StyledHeader = styled.header<{ $hideProjectSwitcher: boolean }>`
	display: grid;
	grid-gap: var(--space-3);
	/* The share column is auto, not 90px: it holds a bordered button now, whose
	   width is its icon, its gap and a translated label. German runs longer than
	   English, so a fixed track would clip it. */
	/* The project and scene tracks are minmax, not a fixed width: their names are
	   user-supplied and long, so they take 280px when the viewport can spare it
	   and fall back to the old 200px when it cannot. A fixed 280 overflowed the
	   header between 1000 and 1080px, which is where the switchers are still
	   shown (they disappear below 999px). The 1fr spacer only gets what is left
	   after these have grown — flexible tracks are expanded last. */
	/* The appointment track is auto like share, and collapses to nothing when
	   BmsAppointmentButton renders its empty span — which it does for everyone
	   except BMS b2c endcustomers. The track has to exist in both cases: the
	   button places implicitly, so dropping the track would shift share and
	   user left by one. The Live View track works the same way: auto, empty
	   except in the b2c editor (LiveViewButton). */
	grid-template-columns: ${({ $hideProjectSwitcher }) =>
		$hideProjectSwitcher
			? "auto minmax(200px, 280px) 1fr auto auto auto 110px"
			: "auto minmax(200px, 280px) minmax(200px, 280px) 1fr auto auto auto 110px"};
	grid-template-areas: ${({ $hideProjectSwitcher }) =>
		$hideProjectSwitcher
			? '"organizations scenes spacing liveview appointment share user"'
			: '"organizations projects scenes spacing liveview appointment share user"'};
	align-items: center;

	/* Button's "inline" variant sets align-self: flex-start so a flex column does
	   not stretch it. This is a grid row, where that reads as top-aligned —
	   alignment belongs to the container, so the container states it. */
	.liveViewButton,
	.shareButton,
	.appointmentButton {
		align-self: center;
	}

	background-color: var(--color-surface);
	height: var(--size-topbar);
	padding: 0 var(--space-5);
	border-bottom: var(--border-strong);
	position: sticky;
	top: 0;
	z-index: var(--z-appbar);
	@media (max-width: 999px) {
		display: flex;
		justify-content: space-between;
		.projectSwitcher,
		.sceneSwitcher,
		.shareButton {
			display: none;
		}
	}
`;

// A button, not a link: the click is guarded by a confirmation when the scene
// has unsaved changes, so it cannot be an <a> the browser may follow first.
const StyledLogoButton = styled.button`
	display: flex;
	align-items: center;
	padding: 0;
	background: none;
	border: none;
	cursor: pointer;
	/* No :focus-visible rule here — tokens.css styles every focusable element
	   with --focus-ring globally; this only shapes where that ring lands. */
	border-radius: var(--radius-sm);
`;

const StyledOrganizationSwitcherContainer = styled.div`
	display: flex;
	align-items: center;

	@media (max-width: 999px) {
		width: 100%;
		> div {
			max-width: 100%;
		}
	}
	/* The logo is height-constrained only: org avatars come in whatever aspect
	   ratio the customer uploaded, and a wide wordmark should be allowed to use
	   the horizontal space it needs rather than being squeezed. The grid's
	   "organizations" track is auto-sized, so it grows with it. Was 44px while
	   logos were stored padded into a square; they are stored trimmed now. */
	img {
		height: var(--size-header-logo);
		width: auto;
		max-width: none;
		flex-shrink: 0;
		object-fit: contain;
		padding: 0 10px 0 0;
	}
`;

export default Header;
